import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { getKnowledgeVisualProfile } from './api';
import type { KnowledgeVisualProfileResponse } from './types';

function displayDate(value?: string): string {
  if (!value) return '–';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('sv-SE');
}

function stringValues(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
}

const VISUAL_TERM_LABELS: Record<string, string> = {
  topDown: 'ovanifrån',
  highAngle: 'högt perspektiv',
  eyeLevel: 'ögonhöjd',
  lowAngle: 'lågt perspektiv',
  front: 'framifrån',
  side: 'från sidan',
  back: 'bakifrån',
  threeQuarter: 'trekvartsvinkel',
  productShot: 'produktbild',
  environmentImage: 'miljöbild',
  chef: 'kock',
  cooking: 'matlagning',
  storeDisplay: 'butiksexponering',
  event: 'event',
  visibleIngredient: 'synlig ingrediens',
  blue: 'blå',
  red: 'röd',
  green: 'grön',
  unknown: '',
  notApplicable: '',
  flatDigital: 'digitalt original',
  scanned: 'skannat original',
  angledPhoto: 'vinklat foto',
  physicalEnvironment: 'fysisk miljö',
  fullShot: 'helbild',
  mediumShot: 'halvbild',
  wideShot: 'vidbild',
  extremeCloseUp: 'extrem närbild',
  closeUp: 'närbild',
  landscape: 'liggande format',
  portrait: 'stående format',
  square: 'kvadratiskt format',
  center: 'centrerad',
  left: 'vänsterplacerad',
  right: 'högerplacerad',
  distributed: 'fördelad placering',
  shallow: 'kort skärpedjup',
  deep: 'djupt skärpedjup',
  normal: 'normal betraktarposition',
};

function displayTerm(value: unknown): string {
  const text = String(value || '').trim();
  if (!text) return '';
  return VISUAL_TERM_LABELS[text] || text;
}

type ProfileDisplaySection = {
  title: string;
  values: unknown[];
  empty?: string;
};

function uniqueDisplayValues(values: unknown[]): string[] {
  const seen = new Set<string>();
  return values
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .map(displayTerm)
    .filter((value) => {
      if (/^\d+(\.\d+)?$/.test(value)) return false;
      if (value.length > 54 || value.split(/\s+/).length > 7) return false;
      const key = value.toLocaleLowerCase('sv-SE');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

const ProfileSection: React.FC<{ title: string; values: unknown[]; empty?: string }> = ({
  title,
  values,
  empty = '–',
}) => {
  const items = uniqueDisplayValues(values);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      {items.length > 0 ? (
        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
          {items.map((item) => <Chip key={item} size="small" variant="outlined" label={item} />)}
        </Stack>
      ) : (
        <Typography variant="body2">{empty}</Typography>
      )}
    </Box>
  );
};

function buildProfileSections(
  family: string,
  visualProfile: Record<string, any>,
  composition: Record<string, any>,
  peopleTerms: string[],
): ProfileDisplaySection[] {
  const categoryData = visualProfile.categoryData || {};
  const technicalColors = stringValues(categoryData.technicalColors);
  const colorSystems = stringValues(categoryData.colorSystems);
  const compositionCore = [
    composition.cameraAngle,
    composition.viewDirection,
    composition.viewpoint,
    composition.framing,
    composition.layoutOrientation,
    composition.motifPlacement,
  ];

  if (family === 'logo') {
    return [
      { title: 'Logotyp och varumärke', values: [visualProfile.imageType, visualProfile.scene, visualProfile.visibleProductsBrands] },
      { title: 'Läsbar text', values: [visualProfile.observedText], empty: 'Ingen tydlig text observerad.' },
      { title: 'Färg och tryckteknik', values: [visualProfile.prominentColors, technicalColors, colorSystems] },
      { title: 'Variant och användning', values: [visualProfile.mood, visualProfile.useCases] },
      { title: 'Grafisk komposition', values: [composition.viewDirection, composition.motifPlacement, composition.layoutOrientation, composition.foreground, composition.middleGround, composition.background] },
    ];
  }

  if (family === 'product' || family === 'wine') {
    return [
      { title: 'Produkt och förpackning', values: [visualProfile.imageType, visualProfile.scene, visualProfile.visibleProductsBrands] },
      { title: 'Läsbar etiketttext', values: [visualProfile.observedText], empty: 'Ingen tydlig etiketttext observerad.' },
      { title: 'Synliga råvaror och serveringsbild', values: [visualProfile.foodIngredients, visualProfile.visibleIngredients] },
      { title: 'Förpackningsfärg och teknik', values: [visualProfile.prominentColors, technicalColors, colorSystems] },
      { title: 'Fotovinkel och komposition', values: compositionCore },
    ];
  }

  if (family === 'code') {
    return [
      { title: 'Kodtyp och läsbarhet', values: [visualProfile.imageType, visualProfile.scene, visualProfile.observedText] },
      { title: 'Kontrast och färg', values: [visualProfile.prominentColors] },
      { title: 'Orientering och komposition', values: compositionCore },
      { title: 'Användning', values: [visualProfile.useCases, visualProfile.mood] },
    ];
  }

  if (family === 'icon') {
    return [
      { title: 'Symbol och motiv', values: [visualProfile.imageType, visualProfile.scene, visualProfile.observedText] },
      { title: 'Grafisk stil och färg', values: [visualProfile.prominentColors, visualProfile.mood] },
      { title: 'Tänkt användning', values: [visualProfile.useCases] },
      { title: 'Komposition', values: compositionCore },
    ];
  }

  if (family === 'retailMaterial') {
    return [
      { title: 'Material och budskap', values: [visualProfile.imageType, visualProfile.scene, visualProfile.observedText] },
      { title: 'Synliga produkter och varumärken', values: [visualProfile.visibleProductsBrands, visualProfile.foodIngredients] },
      { title: 'Färg och tryckteknik', values: [visualProfile.prominentColors, technicalColors, colorSystems] },
      { title: 'Användning', values: [visualProfile.useCases, visualProfile.mood] },
      { title: 'Komposition', values: compositionCore },
    ];
  }

  if (family === 'store') {
    return [
      { title: 'Butiksmiljö och exponering', values: [visualProfile.imageType, visualProfile.scene, visualProfile.environments] },
      { title: 'Synliga produkter och varumärken', values: [visualProfile.visibleProductsBrands, visualProfile.foodIngredients] },
      { title: 'Aktivitet och säljläge', values: [visualProfile.activities, visualProfile.useCases, visualProfile.mood] },
      { title: 'Färg och skyltning', values: [visualProfile.prominentColors, visualProfile.observedText] },
      { title: 'Komposition', values: compositionCore },
    ];
  }

  if (family === 'event') {
    return [
      { title: 'Event och sammanhang', values: [visualProfile.imageType, visualProfile.scene, visualProfile.environments] },
      { title: 'Personer och aktivitet', values: [peopleTerms, visualProfile.activities, visualProfile.clothing] },
      { title: 'Produkter och mat', values: [visualProfile.visibleProductsBrands, visualProfile.foodIngredients, visualProfile.visibleIngredients] },
      { title: 'Stämning och användning', values: [visualProfile.mood, visualProfile.useCases] },
      { title: 'Komposition', values: compositionCore },
    ];
  }

  if (family === 'video') {
    return [
      { title: 'Videomotiv och scen', values: [visualProfile.imageType, visualProfile.scene, visualProfile.environments] },
      { title: 'Aktivitet och rörelse', values: [visualProfile.activities, visualProfile.useCases, visualProfile.mood] },
      { title: 'Personer, produkter och mat', values: [peopleTerms, visualProfile.visibleProductsBrands, visualProfile.foodIngredients] },
      { title: 'Text i bild', values: [visualProfile.observedText], empty: 'Ingen tydlig text observerad.' },
      { title: 'Utsnitt och komposition', values: compositionCore },
    ];
  }

  if (family === 'recipe') {
    return [
      { title: 'Maträtt och servering', values: [visualProfile.imageType, visualProfile.scene, visualProfile.foodIngredients] },
      { title: 'Synliga råvaror', values: [visualProfile.visibleIngredients, visualProfile.visibleProductsBrands] },
      { title: 'Miljö och känsla', values: [visualProfile.environments, visualProfile.mood, visualProfile.useCases] },
      { title: 'Färg och komposition', values: [visualProfile.prominentColors, compositionCore] },
      { title: 'Observerad text i bilden', values: [visualProfile.observedText], empty: 'Ingen tydlig text observerad.' },
    ];
  }

  return [
    { title: 'Miljö och scen', values: [visualProfile.imageType, visualProfile.scene, visualProfile.environments] },
    { title: 'Aktivitet och situation', values: [visualProfile.activities, visualProfile.mood, visualProfile.useCases] },
    { title: 'Personer och kläder', values: [peopleTerms, visualProfile.clothing] },
    { title: 'Mat, råvaror och synliga varumärken', values: [visualProfile.foodIngredients, visualProfile.visibleIngredients, visualProfile.visibleProductsBrands] },
    { title: 'Komposition', values: compositionCore },
    { title: 'Observerad text i bilden', values: [visualProfile.observedText], empty: 'Ingen tydlig text observerad.' },
  ];
}

const VisualAssetProfilePanel: React.FC<{ assetId: string }> = ({ assetId }) => {
  const [data, setData] = React.useState<KnowledgeVisualProfileResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getKnowledgeVisualProfile(assetId)
      .then((response) => {
        if (active) setData(response);
      })
      .catch((requestError) => {
        if (!active) return;
        if (requestError?.response?.status === 404) setData({ profile: null, latestJob: null });
        else setError(requestError?.response?.data?.message || requestError?.message || 'Athena-bildprofilen kunde inte hämtas.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [assetId]);

  const profile = data?.profile;
  const visualProfile = profile?.metadata?.visualProfile || {};
  const composition = visualProfile.composition || {};
  const canonicalTerms = stringValues(profile?.metadata?.canonicalTerms);
  const keywords = stringValues(profile?.search?.keywords);
  const synonyms = stringValues(profile?.search?.synonyms);
  const profileFamily = String(profile?.metadata?.profileFamily || '');
  const peopleTerms = stringValues(visualProfile.people?.flatMap((person: Record<string, unknown>) => [
    person.role,
    person.activity,
    person.clothing,
  ]));
  const profileSections = buildProfileSections(profileFamily, visualProfile, composition, peopleTerms);

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2.5, borderColor: '#d8e3ef', bgcolor: '#fbfdff' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>Athena-bildprofil</Typography>
            <Typography variant="caption" color="text.secondary">
              Read-only Knowledge Hub-data som endast används av Chatta med Athena.
            </Typography>
          </Box>
        </Stack>
        {loading && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && !profile && (
          <Alert severity={data?.latestJob?.item?.error ? 'warning' : 'info'}>
            {data?.latestJob?.item?.error
              ? `Senaste försök misslyckades: ${data.latestJob.item.error}`
              : 'Ingen Athena-bildprofil har skapats för denna asset.'}
          </Alert>
        )}
        {profile && (
          <>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip size="small" color={profile.status === 'searchable' ? 'success' : 'default'} label={profile.status} />
              {profile.metadata?.profileFamily && <Chip size="small" variant="outlined" label={String(profile.metadata.profileFamily)} />}
              <Chip size="small" variant="outlined" label={`Schema ${profile.schemaVersion}`} />
              {profile.metadata?.synonymVersion && <Chip size="small" variant="outlined" label={`Synonymer ${String(profile.metadata.synonymVersion)}`} />}
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">Visuell beskrivning</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>{profile.summaryShort}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, mt: 0.75 }}>
                {profile.summaryDetailed}
              </Typography>
            </Box>
            {stringValues(profile.warnings).length > 0 && (
              <Alert severity="warning">{stringValues(profile.warnings).join(' • ')}</Alert>
            )}
            <Divider />
            <Stack spacing={1.5}>
              {profileSections.map((section) => (
                <ProfileSection
                  key={section.title}
                  title={section.title}
                  values={section.values}
                  empty={section.empty}
                />
              ))}
              <Box>
                <Typography variant="caption" color="text.secondary">Generering</Typography>
                <Typography variant="body2">
                  {[profile.generation?.model, profile.generation?.promptVersion, displayDate(profile.generation?.generatedAt)]
                    .filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Stack>
            <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" fontWeight={700}>Intern sökdata för Chatta med Athena</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <Alert severity="info" sx={{ py: 0.75 }}>
                    Dessa termer används för sök, synonymmatchning och Qdrant-vectorisering. De kan innehålla kontrollerade svenska/engelska alias och är inte tänkta som den primära läsvyn.
                  </Alert>
                  {canonicalTerms.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Kanoniska termer</Typography>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {canonicalTerms.map((term) => <Chip key={term} size="small" color="primary" variant="outlined" label={displayTerm(term)} />)}
                      </Stack>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Sökord</Typography>
                    <Typography variant="body2">{keywords.join(', ') || '–'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Kontrollerade alias</Typography>
                    <Typography variant="body2">{synonyms.join(', ') || '–'}</Typography>
                  </Box>
                  {Object.keys(profile.confidence || {}).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Confidence</Typography>
                      <Typography variant="body2">
                        {Object.entries(profile.confidence || {}).map(([key, value]) => `${key}: ${Number(value).toFixed(2)}`).join(' · ')}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default VisualAssetProfilePanel;
